/**
 * TaxLiability — the new Tax Liability Reporting Centre (Slice 1 of 3).
 *
 * Three views live inside one route:
 *   • Dashboard   — searchable list, latest snapshot per client
 *   • Snapshot    — capture / refresh a myIR snapshot (immutable, versioned)
 *   • Preview     — mandatory onscreen report preview before any delivery
 *
 * Slice 2 will wire Email / Publish to portal from the Preview view. For
 * now the Preview shows Save-Draft / Approve; delivery buttons are
 * disabled with a hint.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Plus, Search, X, FileText, ArrowLeft, Save, CheckCircle2, ShieldAlert, Printer, Trash2, ExternalLink, Image as ImageIcon, Upload } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const TYPES = [
    { key: "gst", label: "GST Liability" },
    { key: "income_tax", label: "Income Tax Liability" },
    { key: "provisional_tax", label: "Provisional Tax (incl. AIM)" },
    { key: "paye", label: "PAYE Liability" },
    { key: "other_ird", label: "Other IRD Debt" },
];
const PROV_METHODS = ["AIM", "Standard", "Estimation", "Ratio"];
const STATUS_META = {
    paid:     { label: "Paid",     chip: "bg-emerald-50 text-emerald-800 border-emerald-200", amount: "text-emerald-700" },
    overdue:  { label: "Overdue",  chip: "bg-red-50 text-red-800 border-red-200",             amount: "text-red-700" },
    upcoming: { label: "Upcoming", chip: "bg-zinc-100 text-zinc-700 border-zinc-200",         amount: "text-[#0F1115]" },
};
const SOURCES = [
    { key: "myir", label: "myIR" },
    { key: "ird_statement", label: "IRD Statement" },
    { key: "client_correspondence", label: "Client Correspondence" },
    { key: "assessment_notice", label: "Assessment Notice" },
    { key: "other", label: "Other" },
];
const REPORT_STATUS_LABELS = {
    draft: "Draft", reviewed: "Reviewed", approved: "Approved",
    sent: "Sent", published: "Published", archived: "Archived",
};

const nzMoney = (v) => v == null ? "—" : `NZ$${Number(v).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const nzDate = (iso) => !iso ? "—" : (() => { try { return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso.slice(0, 10); } })();
const todayIso = () => new Date().toISOString().slice(0, 10);

/** Compute overdue amount + status for a line item (mirror of backend). */
function deriveItemStatus(item) {
    const amount = Number(item.amount || 0);
    const paid = Number(item.amount_paid || 0);
    const overdue = Math.max(0, Math.round((amount - paid) * 100) / 100);
    if (amount > 0 && paid >= amount) return { status: "paid", overdue: 0 };
    if (item.is_overdue) return { status: "overdue", overdue };
    const today = todayIso();
    if (item.due_date && item.due_date < today && overdue > 0) return { status: "overdue", overdue };
    return { status: "upcoming", overdue: 0 };
}

/** Default row factories per category. */
function newOverdueItem(category) {
    return { id: `tmp-${Math.random().toString(36).slice(2, 9)}`, category, payment_type: "Overdue", amount: "", amount_paid: "", due_date: todayIso(), is_overdue: true, period_label: "", method: "", description: "" };
}
function newPaymentItem(category) {
    const base = { id: `tmp-${Math.random().toString(36).slice(2, 9)}`, category, payment_type: "", amount: "", amount_paid: "", due_date: "", is_overdue: false, period_label: "", method: "", description: "" };
    if (category === "paye" || category === "gst") base.payment_type = "Next due";
    if (category === "provisional_tax") { base.method = "AIM"; base.payment_type = "Instalment 1"; }
    if (category === "income_tax") base.payment_type = "Terminal tax";
    if (category === "other_ird") base.payment_type = "Balance";
    return base;
}

export default function TaxLiability() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin" || user?.role === "super_admin";
    const [view, setView] = useState({ name: "dashboard" });
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [snapOpen, setSnapOpen] = useState(false);
    const [snapSeed, setSnapSeed] = useState(null);
    const [adminOpen, setAdminOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            if (status !== "all") params.set("status", status);
            const { data } = await api.get(`/tax-liability/dashboard?${params.toString()}`);
            setRows(data.rows || []);
            setSummary(data.summary || {});
            setSelectedIds((prev) => {
                const validIds = new Set((data.rows || []).map((r) => r.client_id));
                const next = new Set();
                prev.forEach((id) => { if (validIds.has(id)) next.add(id); });
                return next;
            });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [q, status]);

    useEffect(() => { if (view.name === "dashboard") load(); }, [view.name, load]);

    if (view.name === "preview") {
        return <ReportPreview reportId={view.reportId} onBack={() => setView({ name: "dashboard" })} />;
    }

    return (
        <div className="space-y-4">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-semibold text-[#0F1115] tracking-tight">Tax Liability Reporting Centre</h1>
                    <p className="text-sm text-[#5E636E] mt-0.5">
                        Capture myIR balances, review the report onscreen, then send to the client.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {isAdmin && <Button variant="outline" onClick={() => setAdminOpen(true)} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="tl-admin-open">
                        <ShieldAlert className="h-4 w-4 mr-1" /> Admin
                    </Button>}
                    <Button variant="outline" onClick={load} disabled={loading} data-testid="tl-refresh">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCcw className="h-4 w-4 mr-1" /> Refresh</>}
                    </Button>
                    <Button onClick={() => { setSnapSeed(null); setSnapOpen(true); }} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tl-new">
                        <Plus className="h-4 w-4 mr-1" /> New Liability Snapshot
                    </Button>
                </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Kpi label="Clients tracked" value={summary.clients || 0} tone="text-[#0F1115] bg-white border-[#E4E4E7]" />
            </div>

            <Card className="p-3 flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search className="h-3.5 w-3.5 text-[#5E636E] absolute left-2.5 top-2.5" />
                    <Input placeholder="Search client…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pl-7 w-64" data-testid="tl-search" />
                </div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border border-[#E4E4E7] bg-white text-[13px] px-2" data-testid="tl-status">
                    <option value="all">Any report status</option>
                    {Object.entries(REPORT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {(q || status !== "all") && (
                    <Button variant="ghost" size="sm" onClick={() => { setQ(""); setStatus("all"); }} data-testid="tl-clear"><X className="h-3 w-3 mr-1" /> Clear</Button>
                )}
            </Card>

            <Card className="p-0 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-sm text-[#5E636E]" data-testid="tl-empty">
                        No liability snapshots yet. Tap <b>New Liability Snapshot</b> to capture your first myIR balance.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <ResizableTaxLiabilityTable
                            rows={rows}
                            isAdmin={isAdmin}
                            selectedIds={selectedIds}
                            setSelectedIds={setSelectedIds}
                            onRefresh={(r) => { setSnapSeed(r); setSnapOpen(true); }}
                            onPreview={(rep) => setView({ name: "preview", reportId: rep.report.id })}
                        />
                    </div>
                )}
            </Card>

            <SnapshotDialog open={snapOpen} onClose={() => setSnapOpen(false)} seed={snapSeed} onSaved={() => { setSnapOpen(false); load(); }} />
            {isAdmin && <AdminClearDialog open={adminOpen} onClose={() => setAdminOpen(false)} onDone={load} />}
            {isAdmin && (
                <BulkDeleteDialog
                    open={bulkDeleteOpen}
                    onClose={() => setBulkDeleteOpen(false)}
                    clients={rows.filter((r) => selectedIds.has(r.client_id)).map((r) => ({ id: r.client_id, name: r.client_name }))}
                    onDone={() => { setBulkDeleteOpen(false); setSelectedIds(new Set()); load(); }}
                />
            )}
            {isAdmin && selectedIds.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-full border border-[#0F1115] bg-[#0F1115] text-white shadow-xl px-4 py-2" data-testid="tl-bulk-bar">
                    <span className="text-[13px] font-medium">{selectedIds.size} client{selectedIds.size === 1 ? "" : "s"} selected</span>
                    <span className="h-4 w-px bg-white/20" />
                    <button onClick={() => setSelectedIds(new Set())} className="text-[12px] text-white/70 hover:text-white" data-testid="tl-bulk-clear">Clear</button>
                    <Button size="sm" onClick={() => setBulkDeleteOpen(true)} className="h-7 text-[12px] bg-red-600 hover:bg-red-700 text-white" data-testid="tl-bulk-delete">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete selected
                    </Button>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, tone }) {
    return (
        <div className={`rounded-xl border p-3 ${tone}`}>
            <div className="text-[11px] uppercase tracking-wider opacity-80">{label}</div>
            <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
        </div>
    );
}

/* -------- Resizable table with per-user persisted column widths -------- */
const TL_COLS = [
    { id: "client",      label: "Client",       align: "left",  width: 220, min: 120, sortKey: (r) => (r.client_name || "").toLowerCase() },
    { id: "gst",         label: "GST",          align: "right", width: 100, min: 70,  sortKey: (r) => Number(r.gst_liability) || 0 },
    { id: "income_tax",  label: "Income Tax",   align: "right", width: 110, min: 80,  sortKey: (r) => Number(r.income_tax_liability) || 0 },
    { id: "provisional_tax", label: "Prov Tax", align: "right", width: 100, min: 70,  sortKey: (r) => Number(r.provisional_tax_liability) || 0 },
    { id: "paye",        label: "PAYE",         align: "right", width: 90,  min: 70,  sortKey: (r) => Number(r.paye_liability) || 0 },
    { id: "other_ird",   label: "Other IRD",    align: "right", width: 100, min: 70,  sortKey: (r) => Number(r.other_ird?.amount) || 0 },
    { id: "total",       label: "Total",        align: "right", width: 110, min: 80,  sortKey: (r) => Number(r.total_liability) || 0 },
    { id: "overdue",     label: "Overdue",      align: "right", width: 100, min: 70,  sortKey: (r) => Number(r.overdue_amount) || 0 },
    { id: "updated",     label: "Last updated", align: "left",  width: 140, min: 100, sortKey: (r) => r.captured_at || "" },
    { id: "report",      label: "Report",       align: "left",  width: 100, min: 80,  sortKey: (r) => r.report_status || "zzz_none" },
    { id: "actions",     label: "Actions",      align: "right", width: 220, min: 180 },
];
const TL_COL_STORAGE_KEY = "clientops.tl.colwidths.v1";
const TL_SORT_STORAGE_KEY = "clientops.tl.sort.v1";

function loadColWidths() {
    try {
        const raw = localStorage.getItem(TL_COL_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        return parsed;
    } catch { return null; }
}

function loadSort(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return { col: null, dir: null };
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.col) return { col: null, dir: null };
        return { col: parsed.col, dir: parsed.dir || "asc" };
    } catch { return { col: null, dir: null }; }
}

/** Tri-state cycle: null → asc → desc → null. */
function nextSort(current, colId) {
    if (current.col !== colId) return { col: colId, dir: "asc" };
    if (current.dir === "asc")  return { col: colId, dir: "desc" };
    return { col: null, dir: null };
}

function sortRows(rows, sort, colDefs) {
    if (!sort.col) return rows;
    const col = colDefs.find((c) => c.id === sort.col);
    if (!col || !col.sortKey) return rows;
    const mul = sort.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
        const av = col.sortKey(a); const bv = col.sortKey(b);
        if (av === bv) return 0;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * mul;
        return String(av).localeCompare(String(bv)) * mul;
    });
}

function ResizableTaxLiabilityTable({ rows, isAdmin, selectedIds, setSelectedIds, onRefresh, onPreview }) {
    const stored = loadColWidths();
    const [widths, setWidths] = useState(() => {
        const base = {};
        TL_COLS.forEach((c) => { base[c.id] = (stored && stored[c.id]) || c.width; });
        return base;
    });
    const [sort, setSort] = useState(() => loadSort(TL_SORT_STORAGE_KEY));
    const dragRef = useRef(null); // { colId, startX, startW }

    useEffect(() => {
        try { localStorage.setItem(TL_SORT_STORAGE_KEY, JSON.stringify(sort)); } catch { /* ignore */ }
    }, [sort]);

    const sortedRows = useMemo(() => sortRows(rows, sort, TL_COLS), [rows, sort]);

    function onDragStart(e, colId) {
        e.preventDefault();
        dragRef.current = { colId, startX: e.clientX, startW: widths[colId] };
        window.addEventListener("mousemove", onDragMove);
        window.addEventListener("mouseup", onDragEnd, { once: true });
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }
    function onDragMove(e) {
        if (!dragRef.current) return;
        const { colId, startX, startW } = dragRef.current;
        const min = TL_COLS.find((c) => c.id === colId)?.min || 60;
        const next = Math.max(min, Math.round(startW + (e.clientX - startX)));
        setWidths((w) => (w[colId] === next ? w : { ...w, [colId]: next }));
    }
    function onDragEnd() {
        window.removeEventListener("mousemove", onDragMove);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        try { localStorage.setItem(TL_COL_STORAGE_KEY, JSON.stringify(widths)); } catch { /* ignore */ }
        dragRef.current = null;
    }
    // Persist on width change too, so the drag-end save is a safety net.
    useEffect(() => {
        try { localStorage.setItem(TL_COL_STORAGE_KEY, JSON.stringify(widths)); } catch { /* ignore */ }
    }, [widths]);

    function reset() {
        const base = {};
        TL_COLS.forEach((c) => { base[c.id] = c.width; });
        setWidths(base);
    }

    // Total table width from user's widths so the horizontal scroll works.
    const totalWidth = (isAdmin ? 40 : 0) + TL_COLS.reduce((n, c) => n + widths[c.id], 0);

    return (
        <div className="min-w-0">
            <div className="flex items-center justify-end pb-1 pr-1">
                <button
                    type="button"
                    onClick={reset}
                    className="text-[11px] text-[#5E636E] hover:text-[#0F1115] underline decoration-dotted"
                    data-testid="tl-col-reset"
                    title="Restore default column widths"
                >
                    Reset columns
                </button>
            </div>
            <table
                className="text-[13px] border-separate border-spacing-0"
                style={{ tableLayout: "fixed", width: `${totalWidth}px`, minWidth: "100%" }}
                data-testid="tl-table"
            >
                <colgroup>
                    {isAdmin && <col style={{ width: "40px" }} />}
                    {TL_COLS.map((c) => (
                        <col key={c.id} style={{ width: `${widths[c.id]}px` }} />
                    ))}
                </colgroup>
                <thead className="bg-[#FAFAF7] text-[11.5px] uppercase tracking-wider text-[#5E636E]">
                    <tr>
                        {isAdmin && (
                            <th className="text-left px-3 py-2 font-medium">
                                <input
                                    type="checkbox"
                                    aria-label="Select all clients"
                                    checked={rows.length > 0 && selectedIds.size === rows.length}
                                    ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < rows.length; }}
                                    onChange={(e) => {
                                        if (e.target.checked) setSelectedIds(new Set(rows.map((r) => r.client_id)));
                                        else setSelectedIds(new Set());
                                    }}
                                    className="h-3.5 w-3.5 accent-[#0F1115] cursor-pointer"
                                    data-testid="tl-select-all"
                                />
                            </th>
                        )}
                        {TL_COLS.map((c) => {
                            const sortable = !!c.sortKey;
                            const active = sort.col === c.id;
                            const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "";
                            return (
                                <th
                                    key={c.id}
                                    className={`relative px-3 py-2 font-medium ${c.align === "right" ? "text-right" : "text-left"} ${sortable ? "cursor-pointer select-none hover:text-[#0F1115]" : ""}`}
                                    onClick={sortable ? () => setSort((s) => nextSort(s, c.id)) : undefined}
                                    data-testid={`tl-th-${c.id}`}
                                    title={sortable ? "Click to sort" : undefined}
                                >
                                    <span className="truncate inline-flex items-center gap-1">
                                        {c.label}
                                        {arrow && <span className="text-[#0F1115]">{arrow}</span>}
                                    </span>
                                    <span
                                        onMouseDown={(e) => { e.stopPropagation(); onDragStart(e, c.id); }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="absolute top-0 right-0 h-full w-2 cursor-col-resize select-none group"
                                        data-testid={`tl-resize-${c.id}`}
                                        title="Drag to resize"
                                    >
                                        <span className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-px bg-[#E4E4E7] group-hover:bg-[#0F1115]" />
                                    </span>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((r) => (
                        <tr key={r.id} className={`border-t border-[#EEEDE7] hover:bg-[#FAFAF7] ${selectedIds.has(r.client_id) ? "bg-red-50/40" : ""}`} data-testid={`tl-row-${r.client_id}`}>
                            {isAdmin && (
                                <td className="px-3 py-2 border-t border-[#EEEDE7]">
                                    <input
                                        type="checkbox"
                                        aria-label={`Select ${r.client_name}`}
                                        checked={selectedIds.has(r.client_id)}
                                        onChange={(e) => {
                                            setSelectedIds((prev) => {
                                                const next = new Set(prev);
                                                if (e.target.checked) next.add(r.client_id);
                                                else next.delete(r.client_id);
                                                return next;
                                            });
                                        }}
                                        className="h-3.5 w-3.5 accent-[#0F1115] cursor-pointer"
                                        data-testid={`tl-select-${r.client_id}`}
                                    />
                                </td>
                            )}
                            <td className="px-3 py-2 border-t border-[#EEEDE7] overflow-hidden">
                                <div className="font-medium text-[#0F1115] truncate" title={r.client_name}>{r.client_name}</div>
                                <div className="text-[11px] text-[#5E636E] truncate">IRD {r.ird_number || "—"} · v{r.version_number}</div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums border-t border-[#EEEDE7] truncate">{nzMoney(r.gst_liability)}</td>
                            <td className="px-3 py-2 text-right tabular-nums border-t border-[#EEEDE7] truncate">{nzMoney(r.income_tax_liability)}</td>
                            <td className="px-3 py-2 text-right tabular-nums border-t border-[#EEEDE7] truncate">{nzMoney(r.provisional_tax_liability)}</td>
                            <td className="px-3 py-2 text-right tabular-nums border-t border-[#EEEDE7] truncate">{nzMoney(r.paye_liability)}</td>
                            <td className="px-3 py-2 text-right tabular-nums border-t border-[#EEEDE7] truncate">{nzMoney(r.other_ird?.amount)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold border-t border-[#EEEDE7] truncate">{nzMoney(r.total_liability)}</td>
                            <td className={`px-3 py-2 text-right tabular-nums border-t border-[#EEEDE7] truncate ${Number(r.overdue_amount) > 0 ? "text-red-700 font-semibold" : "text-[#5E636E]"}`}>{nzMoney(r.overdue_amount)}</td>
                            <td className="px-3 py-2 text-[12px] text-[#5E636E] border-t border-[#EEEDE7] overflow-hidden">
                                <div className="truncate">{nzDate(r.captured_at)}</div>
                                <div className="text-[10.5px] truncate">by {r.prepared_by_name || "—"}</div>
                            </td>
                            <td className="px-3 py-2 text-[12px] border-t border-[#EEEDE7] overflow-hidden">
                                {r.report_status ? (
                                    <span className="rounded-full border border-[#E4E4E7] bg-white px-2 py-0.5 text-[11px] capitalize inline-block max-w-full truncate">{REPORT_STATUS_LABELS[r.report_status] || r.report_status}</span>
                                ) : <span className="text-[#5E636E]">—</span>}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap border-t border-[#EEEDE7]">
                                <div className="inline-flex gap-1">
                                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onRefresh(r)} data-testid={`tl-refresh-${r.client_id}`}>
                                        <RefreshCcw className="h-3 w-3 mr-1" /> Refresh
                                    </Button>
                                    <ReportBuilderTrigger row={r} onBuilt={onPreview} />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* -------- Snapshot capture / refresh dialog -------- */
function SnapshotDialog({ open, onClose, seed, onSaved }) {
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [q, setQ] = useState("");
    const [form, setForm] = useState({ source: "myir", ird_number: "", income_tax_year_end: "", internal_notes: "" });
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [myirText, setMyirText] = useState("");
    const [parsing, setParsing] = useState(false);
    const [myirImage, setMyirImage] = useState(null);
    const [visionParsing, setVisionParsing] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try { const { data } = await api.get("/clients/"); setClients(Array.isArray(data) ? data : data?.rows || []); }
            catch (e) { toast.error(formatApiError(e)); }
        })();
        if (seed) {
            setClientId(seed.client_id);
            setForm({
                source: seed.source || "myir",
                ird_number: seed.ird_number || "",
                income_tax_year_end: seed.income_tax_year_end || `31 Mar ${new Date().getFullYear() + (new Date().getMonth() >= 3 ? 1 : 0)}`,
                internal_notes: "",
            });
            // Seed items from previous snapshot's items array; if legacy
            // flat fields were used, synthesise overdue rows so the firm
            // can edit them.
            if (Array.isArray(seed.items) && seed.items.length > 0) {
                setItems(seed.items.map((it) => ({ ...it, id: it.id || `tmp-${Math.random().toString(36).slice(2, 9)}` })));
            } else {
                const legacy = [];
                if (seed.gst_liability) legacy.push({ ...newOverdueItem("gst"), amount: seed.gst_liability });
                if (seed.income_tax_liability) legacy.push({ ...newOverdueItem("income_tax"), amount: seed.income_tax_liability });
                if (seed.paye_liability) legacy.push({ ...newOverdueItem("paye"), amount: seed.paye_liability });
                const o = seed.other_ird || {};
                if (o.amount) legacy.push({ ...newOverdueItem("other_ird"), amount: o.amount, description: o.description || "" });
                setItems(legacy);
            }
        } else {
            setClientId(""); setQ("");
            const defaultYE = `31 Mar ${new Date().getFullYear() + (new Date().getMonth() >= 3 ? 1 : 0)}`;
            setForm({ source: "myir", ird_number: "", income_tax_year_end: defaultYE, internal_notes: "" });
            setItems([]);
        }
        setMyirText("");
        setMyirImage(null);
    }, [open, seed]);

    useEffect(() => {
        if (!clientId) return;
        const c = clients.find((x) => x.id === clientId);
        if (!c) return;
        setForm((f) => ({ ...f, ird_number: f.ird_number || c.ird_number || "" }));
    }, [clientId, clients]);

    const filteredClients = useMemo(() => {
        const term = q.trim().toLowerCase();
        return term ? clients.filter((c) => (c.name || "").toLowerCase().includes(term)).slice(0, 40) : clients.slice(0, 40);
    }, [clients, q]);

    async function parseMyirPaste() {
        if (!myirText.trim()) { toast.error("Paste the myIR Summary page text first"); return; }
        setParsing(true);
        try {
            const { data } = await api.post("/tax-liability/myir-parse", { text: myirText });
            applyParsedResult(data, "myIR text");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setParsing(false); }
    }

    /** Convert a parsed myIR summary into Overdue items per category. */
    function applyParsedResult(data, sourceLabel) {
        const nextItems = [...items];
        const upsertOverdue = (category, amount, extra = {}) => {
            const idx = nextItems.findIndex((it) => it.category === category && it.is_overdue);
            const row = { ...newOverdueItem(category), amount: String(amount), ...extra };
            if (idx >= 0) nextItems[idx] = { ...nextItems[idx], amount: String(amount), ...extra };
            else nextItems.push(row);
        };
        let filled = 0;
        if (data.gst != null) { upsertOverdue("gst", data.gst); filled++; }
        if (data.income_tax != null) { upsertOverdue("income_tax", data.income_tax); filled++; }
        if (data.paye != null) { upsertOverdue("paye", data.paye); filled++; }
        if (data.other != null) { upsertOverdue("other_ird", data.other, { description: (data.other_types || []).join(", ") }); filled++; }
        if (data.ird_number && !form.ird_number) setForm((f) => ({ ...f, ird_number: data.ird_number }));
        setItems(nextItems);
        if (filled === 0) toast.error(`Couldn't find any account balances in that ${sourceLabel} — check you're on the Summary tab`);
        else toast.success(`Auto-filled ${filled} overdue row${filled === 1 ? "" : "s"} from ${sourceLabel}${data.closed_accounts_skipped ? ` (${data.closed_accounts_skipped} closed account${data.closed_accounts_skipped === 1 ? "" : "s"} skipped)` : ""}`);
    }

    function acceptImageFile(file) {
        if (!file) return;
        if (!/^image\/(png|jpeg|webp)$/.test(file.type)) { toast.error("Screenshot must be PNG, JPEG, or WEBP"); return; }
        if (file.size > 8 * 1024 * 1024) { toast.error("Screenshot too large (max 8 MB)"); return; }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const dataUrl = ev.target.result;
            const b64 = String(dataUrl).split(",", 2)[1];
            setMyirImage({ dataUrl, base64: b64, mime: file.type, filename: file.name || "screenshot" });
        };
        reader.readAsDataURL(file);
    }
    function onPasteZone(ev) {
        const list = ev.clipboardData?.items || [];
        for (const it of list) {
            if (it.kind === "file" && it.type.startsWith("image/")) {
                const file = it.getAsFile();
                if (file) { ev.preventDefault(); acceptImageFile(file); return; }
            }
        }
    }
    function onDropZone(ev) { ev.preventDefault(); const file = ev.dataTransfer?.files?.[0]; if (file) acceptImageFile(file); }

    async function parseMyirImage() {
        if (!myirImage) { toast.error("Paste or drop a myIR screenshot first"); return; }
        setVisionParsing(true);
        try {
            const { data } = await api.post("/tax-liability/myir-parse-image", { image_base64: myirImage.base64, mime_type: myirImage.mime });
            applyParsedResult(data, "myIR screenshot");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setVisionParsing(false); }
    }

    async function submit() {
        if (!clientId) { toast.error("Pick a client first"); return; }
        setBusy(true);
        try {
            // Strip tmp ids and coerce numeric strings before sending.
            const cleanItems = items.map((it) => ({
                category: it.category,
                payment_type: it.payment_type || "",
                amount: it.amount === "" ? 0 : Number(it.amount),
                due_date: it.due_date || null,
                amount_paid: it.amount_paid === "" ? 0 : Number(it.amount_paid),
                is_overdue: !!it.is_overdue,
                period_label: it.period_label || null,
                method: it.method || null,
                description: it.description || null,
            }));
            const body = {
                client_id: clientId,
                ird_number: form.ird_number || null,
                income_tax_year_end: form.income_tax_year_end || null,
                items: cleanItems,
                source: form.source || "myir",
                internal_notes: form.internal_notes || null,
            };
            await api.post("/tax-liability/snapshots", body);
            toast.success(seed ? "New snapshot version saved" : "Snapshot saved");
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    const isMyir = (form.source || "myir") === "myir";
    const grand = useMemo(() => items.reduce((acc, it) => {
        const d = deriveItemStatus(it);
        acc.amount += Number(it.amount || 0);
        acc.paid += Number(it.amount_paid || 0);
        acc.overdue += d.overdue;
        return acc;
    }, { amount: 0, paid: 0, overdue: 0 }), [items]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="tl-snap-dialog">
                <DialogHeader>
                    <DialogTitle>{seed ? `Refresh liability snapshot · ${seed.client_name}` : "New liability snapshot"}</DialogTitle>
                    <DialogDescription>
                        Add rows per tax type — Overdue rows roll up all unpaid historical amounts and auto-set due date to today; the next-due or AIM rows carry their own period and date.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {!seed && (
                        <div>
                            <Label className="text-[12px] text-[#5E636E]">Client</Label>
                            <Input placeholder="Search client…" value={q} onChange={(e) => setQ(e.target.value)} className="mt-1" data-testid="tl-snap-search" />
                            <div className="mt-2 max-h-32 overflow-y-auto rounded-md border border-[#E4E4E7]">
                                {filteredClients.map((c) => (
                                    <button key={c.id} type="button" onClick={() => setClientId(c.id)} className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-zinc-50 ${clientId === c.id ? "bg-[#0F1115] text-white hover:bg-[#0F1115]" : ""}`} data-testid={`tl-snap-client-${c.id}`}>
                                        {c.name}{c.ird_number ? <span className="opacity-60 text-[11px] ml-2">IRD {c.ird_number}</span> : null}
                                    </button>
                                ))}
                                {filteredClients.length === 0 && <div className="p-3 text-[12px] text-[#5E636E]">No matches</div>}
                            </div>
                        </div>
                    )}

                    <FieldRow>
                        <Field label="IRD number">
                            <Input value={form.ird_number || ""} onChange={(e) => setForm({ ...form, ird_number: e.target.value })} placeholder={clientId ? "Auto-filled from client record" : "Pick a client first"} data-testid="tl-snap-ird" />
                        </Field>
                        <Field label="Source">
                            <select value={form.source || "myir"} onChange={(e) => setForm({ ...form, source: e.target.value })} className="h-9 w-full rounded-md border border-[#E4E4E7] bg-white text-[13px] px-2" data-testid="tl-snap-source">
                                {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        </Field>
                    </FieldRow>

                    {isMyir && (
                        <div className="rounded-md border border-sky-200 bg-sky-50/60 p-3 space-y-3" data-testid="tl-snap-myir-block">
                            <div className="flex items-center justify-between gap-2">
                                <div>
                                    <div className="text-[12px] font-semibold text-sky-900">Pull from myIR</div>
                                    <div className="text-[11px] text-sky-800/80">Paste the Summary text or a screenshot — unpaid balances arrive as Overdue rows you can then split out.</div>
                                </div>
                                <Button size="sm" variant="outline" className="h-8 text-[12px] shrink-0 border-sky-300 text-sky-900 hover:bg-sky-100" onClick={() => window.open("https://myir.ird.govt.nz/", "_blank", "noopener,noreferrer")} data-testid="tl-snap-open-myir">
                                    <ExternalLink className="h-3 w-3 mr-1" /> Open myIR
                                </Button>
                            </div>
                            <div>
                                <Label className="text-[11px] text-sky-900 font-medium">Screenshot (paste or drop)</Label>
                                <div onPaste={onPasteZone} onDrop={onDropZone} onDragOver={(e) => e.preventDefault()} tabIndex={0}
                                    className="mt-1 rounded-md border border-dashed border-sky-300 bg-white hover:bg-sky-50/40 px-3 py-4 text-center outline-none focus:ring-2 focus:ring-sky-400 cursor-pointer relative"
                                    onClick={() => document.getElementById("tl-snap-file-input")?.click()} data-testid="tl-snap-myir-image-zone">
                                    <input id="tl-snap-file-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                                        onChange={(e) => { const f = e.target.files?.[0]; if (f) acceptImageFile(f); e.target.value = ""; }} />
                                    {myirImage ? (
                                        <div className="flex items-center gap-3 text-left" data-testid="tl-snap-myir-image-preview">
                                            <img src={myirImage.dataUrl} alt="myIR screenshot" className="h-16 w-auto max-w-[120px] rounded border border-sky-200 object-cover" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[12px] font-medium text-sky-900 truncate">{myirImage.filename}</div>
                                                <div className="text-[10.5px] text-sky-800/80">{myirImage.mime.split("/")[1].toUpperCase()} · {Math.round((myirImage.base64.length * 3 / 4) / 1024)} KB</div>
                                            </div>
                                            <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={(e) => { e.stopPropagation(); setMyirImage(null); }} data-testid="tl-snap-myir-image-clear"><X className="h-3 w-3" /></Button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-1 py-1 text-sky-900/80">
                                            <ImageIcon className="h-5 w-5 opacity-60" />
                                            <div className="text-[12px]"><b>Ctrl / ⌘ + V</b> to paste, drop here, or <span className="underline">click to browse</span></div>
                                            <div className="text-[10.5px] opacity-70">PNG · JPEG · WEBP · max 8 MB</div>
                                        </div>
                                    )}
                                </div>
                                {myirImage && (
                                    <div className="flex justify-end mt-2">
                                        <Button size="sm" onClick={parseMyirImage} disabled={visionParsing} className="h-8 text-[12px] bg-sky-700 hover:bg-sky-800 text-white" data-testid="tl-snap-myir-image-parse">
                                            {visionParsing ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Reading screenshot…</> : <><Upload className="h-3 w-3 mr-1" /> Auto-fill from screenshot</>}
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 h-px bg-sky-200" />
                                <div className="text-[10px] text-sky-800/70 uppercase tracking-wider">or paste text</div>
                                <div className="flex-1 h-px bg-sky-200" />
                            </div>
                            <Textarea rows={3} placeholder="Paste the myIR account summary page text…" value={myirText} onChange={(e) => setMyirText(e.target.value)} data-testid="tl-snap-myir-paste" />
                            <div className="flex justify-end">
                                <Button size="sm" onClick={parseMyirPaste} disabled={parsing || !myirText.trim()} className="h-8 text-[12px] bg-sky-700 hover:bg-sky-800 text-white" data-testid="tl-snap-myir-parse">
                                    {parsing ? <Loader2 className="h-3 w-3 animate-spin" /> : <>Auto-fill from text</>}
                                </Button>
                            </div>
                        </div>
                    )}

                    {TYPES.map((t) => (
                        <CategorySection
                            key={t.key}
                            category={t.key}
                            label={t.label}
                            items={items.filter((it) => it.category === t.key)}
                            addItem={(creator) => setItems((all) => [...all, creator(t.key)])}
                            patchItem={(id, patch) => setItems((all) => all.map((it) => it.id === id ? { ...it, ...patch } : it))}
                            removeItem={(id) => setItems((all) => all.filter((it) => it.id !== id))}
                            yearEnd={form.income_tax_year_end}
                            setYearEnd={(v) => setForm((f) => ({ ...f, income_tax_year_end: v }))}
                        />
                    ))}

                    <div className="rounded-md border border-[#0F1115] bg-[#FAFAF7] px-3 py-2 flex items-center justify-between flex-wrap gap-2" data-testid="tl-snap-totals">
                        <div className="text-[12px] uppercase tracking-wider text-[#5E636E] font-medium">Totals</div>
                        <div className="flex gap-4 text-[13px] tabular-nums">
                            <span><span className="text-[#5E636E]">Due</span> <b>{nzMoney(grand.amount)}</b></span>
                            <span><span className="text-[#5E636E]">Paid</span> <b>{nzMoney(grand.paid)}</b></span>
                            <span className="text-red-700"><span className="opacity-80">Overdue</span> <b>{nzMoney(grand.overdue)}</b></span>
                        </div>
                    </div>

                    <Field label="Internal notes (not shown to client)">
                        <Textarea rows={2} value={form.internal_notes || ""} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} data-testid="tl-snap-notes" />
                    </Field>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !clientId} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tl-snap-save">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save snapshot</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- Per-category editable section -------- */
function CategorySection({ category, label, items, addItem, patchItem, removeItem, yearEnd, setYearEnd }) {
    const hasOverdue = items.some((it) => it.is_overdue);
    const isProv = category === "provisional_tax";
    const isOther = category === "other_ird";
    const isIncome = category === "income_tax";
    return (
        <div className="rounded-md border border-[#EEEDE7] bg-white p-3 space-y-2" data-testid={`tl-cat-${category}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[13px] font-semibold text-[#0F1115]">{label}</div>
                <div className="flex gap-1.5">
                    {!hasOverdue && (
                        <Button size="sm" variant="outline" className="h-7 text-[11px] border-red-200 text-red-700 hover:bg-red-50" onClick={() => addItem(newOverdueItem)} data-testid={`tl-cat-${category}-add-overdue`}>
                            + Overdue row
                        </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => addItem(newPaymentItem)} data-testid={`tl-cat-${category}-add-payment`}>
                        + {isProv ? "AIM instalment" : "Payment"}
                    </Button>
                </div>
            </div>
            {isIncome && (
                <div className="flex items-center gap-2 pt-1">
                    <Label className="text-[11.5px] text-[#5E636E] whitespace-nowrap">Year ending</Label>
                    <Input value={yearEnd || ""} onChange={(e) => setYearEnd(e.target.value)} placeholder="e.g. 31 Mar 2026" className="h-8 text-[12.5px] w-44" data-testid="tl-cat-income_tax-ye" />
                </div>
            )}
            {items.length === 0 ? (
                <div className="text-[11.5px] text-[#5E636E] italic">No rows yet — tap the buttons above to add Overdue or payment rows.</div>
            ) : (
                <div className="space-y-2">
                    {items.map((it) => {
                        const d = deriveItemStatus(it);
                        return (
                            <div key={it.id} className={`rounded-md border p-2 ${it.is_overdue ? "border-red-200 bg-red-50/40" : "border-[#EEEDE7] bg-[#FAFAF7]"}`} data-testid={`tl-item-${it.id}`}>
                                <div className="grid grid-cols-12 gap-2 items-end">
                                    <div className="col-span-12 sm:col-span-3">
                                        <Label className="text-[10.5px] text-[#5E636E]">Payment type</Label>
                                        {it.is_overdue ? (
                                            <div className="h-9 flex items-center font-semibold text-red-700 text-[13px]">Overdue</div>
                                        ) : (
                                            <Input value={it.payment_type || ""} onChange={(e) => patchItem(it.id, { payment_type: e.target.value })} placeholder={isProv ? "e.g. Instalment 1" : "Next due"} className="h-9 text-[12.5px]" />
                                        )}
                                    </div>
                                    {isProv && !it.is_overdue && (
                                        <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-[10.5px] text-[#5E636E]">Method</Label>
                                            <select value={it.method || "AIM"} onChange={(e) => patchItem(it.id, { method: e.target.value })} className="h-9 w-full rounded-md border border-[#E4E4E7] bg-white text-[12.5px] px-2">
                                                {PROV_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    {!it.is_overdue && !isIncome && (
                                        <div className={`col-span-6 ${isProv ? "sm:col-span-2" : "sm:col-span-3"}`}>
                                            <Label className="text-[10.5px] text-[#5E636E]">{category === "paye" ? "Return period" : "Period"} {category === "paye" ? "(optional)" : ""}</Label>
                                            <Input value={it.period_label || ""} onChange={(e) => patchItem(it.id, { period_label: e.target.value })} placeholder={category === "gst" ? "e.g. Jul 2026" : category === "paye" ? "e.g. Jul 2026" : ""} className="h-9 text-[12.5px]" />
                                        </div>
                                    )}
                                    <div className="col-span-6 sm:col-span-2">
                                        <Label className="text-[10.5px] text-[#5E636E]">Amount (NZD)</Label>
                                        <Input type="number" step="0.01" value={it.amount ?? ""} onChange={(e) => patchItem(it.id, { amount: e.target.value })} className="h-9 text-[12.5px]" data-testid={`tl-item-${it.id}-amount`} />
                                    </div>
                                    <div className="col-span-6 sm:col-span-2">
                                        <Label className="text-[10.5px] text-[#5E636E]">Due date</Label>
                                        <Input type="date" value={it.due_date || ""} onChange={(e) => patchItem(it.id, { due_date: e.target.value })} disabled={it.is_overdue} className="h-9 text-[12.5px] disabled:bg-zinc-100 disabled:text-[#5E636E]" />
                                    </div>
                                    <div className="col-span-6 sm:col-span-2">
                                        <Label className="text-[10.5px] text-[#5E636E]">Paid (NZD)</Label>
                                        <Input type="number" step="0.01" value={it.amount_paid ?? ""} onChange={(e) => patchItem(it.id, { amount_paid: e.target.value })} className="h-9 text-[12.5px]" />
                                    </div>
                                    <div className="col-span-12 sm:col-span-1 flex items-end justify-end">
                                        <Button size="icon" variant="ghost" className="h-9 w-9 text-[#5E636E] hover:text-red-700" onClick={() => removeItem(it.id)} title="Remove row" data-testid={`tl-item-${it.id}-remove`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                    {isOther && (
                                        <div className="col-span-12">
                                            <Label className="text-[10.5px] text-[#5E636E]">Description</Label>
                                            <Input value={it.description || ""} onChange={(e) => patchItem(it.id, { description: e.target.value })} placeholder="e.g. FBT · RWT · UOMI · Student Loan" className="h-9 text-[12.5px]" />
                                        </div>
                                    )}
                                </div>
                                <div className="mt-2 flex items-center justify-end gap-3 text-[11.5px]">
                                    <span className="text-[#5E636E]">Overdue:</span>
                                    <span className="font-semibold text-red-700 tabular-nums">{nzMoney(d.overdue)}</span>
                                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${STATUS_META[d.status].chip}`}>
                                        {STATUS_META[d.status].label}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* -------- 6-column report lines table -------- */
function LinesTable({ lines, totals, categoryTotals }) {
    const grouped = useMemo(() => {
        const by = {};
        for (const l of (lines || [])) {
            if (!by[l.category]) by[l.category] = [];
            by[l.category].push(l);
        }
        return by;
    }, [lines]);
    const orderedCats = TYPES.map((t) => t.key).filter((k) => grouped[k]);
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="tl-preview-table">
                <thead className="text-[11px] uppercase tracking-wider text-[#5E636E]">
                    <tr>
                        <th className="text-left py-1.5 pr-2">Payment type</th>
                        <th className="text-right py-1.5 pr-2">Amount (NZD)</th>
                        <th className="text-left py-1.5 pr-2">Due date</th>
                        <th className="text-right py-1.5 pr-2">Paid (NZD)</th>
                        <th className="text-right py-1.5 pr-2">Overdue (NZD)</th>
                        <th className="text-left py-1.5">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {orderedCats.map((cat) => (
                        <ReportCategoryRows key={cat} cat={cat} lines={grouped[cat]} subtotal={categoryTotals?.[cat]} />
                    ))}
                    <tr className="border-t-2 border-[#0F1115]">
                        <td className="py-2 font-semibold">Total</td>
                        <td className="py-2 pr-2 text-right tabular-nums font-bold text-[14px]">{nzMoney(totals?.amount)}</td>
                        <td />
                        <td className="py-2 pr-2 text-right tabular-nums font-bold">{nzMoney(totals?.paid)}</td>
                        <td className="py-2 pr-2 text-right tabular-nums font-bold text-red-700">{nzMoney(totals?.overdue)}</td>
                        <td />
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function ReportCategoryRows({ cat, lines, subtotal }) {
    const catLabel = TYPES.find((t) => t.key === cat)?.label || cat;
    return (
        <>
            <tr>
                <td colSpan={6} className="pt-4 pb-1 text-[11px] uppercase tracking-wider text-[#0F1115] font-semibold">
                    {catLabel}
                </td>
            </tr>
            {lines.map((l, idx) => {
                const meta = [];
                if (l.period_label && !l.is_overdue) meta.push(l.period_label);
                if (l.method) meta.push(l.method);
                if (l.description) meta.push(l.description);
                const sm = STATUS_META[l.status] || STATUS_META.upcoming;
                return (
                    <tr key={`${cat}-${idx}`} className={`border-t border-[#EEEDE7] ${l.is_overdue ? "bg-red-50/40" : ""}`}>
                        <td className="py-2 pr-2 align-top">
                            <div className={`font-medium ${l.is_overdue ? "text-red-700" : "text-[#0F1115]"}`}>{l.payment_type}</div>
                            {meta.length > 0 && <div className="text-[11px] text-[#5E636E]">{meta.join(" · ")}</div>}
                        </td>
                        <td className={`py-2 pr-2 text-right tabular-nums ${l.is_overdue ? "text-red-700" : ""}`}>{nzMoney(l.amount)}</td>
                        <td className="py-2 pr-2 text-[#5E636E]">{nzDate(l.due_date)}</td>
                        <td className="py-2 pr-2 text-right tabular-nums text-[#5E636E]">{nzMoney(l.amount_paid)}</td>
                        <td className={`py-2 pr-2 text-right tabular-nums ${l.overdue_amount > 0 ? "text-red-700 font-semibold" : "text-[#5E636E]"}`}>{nzMoney(l.overdue_amount || 0)}</td>
                        <td className="py-2">
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${sm.chip}`}>{sm.label}</span>
                        </td>
                    </tr>
                );
            })}
            {subtotal && lines.length > 1 && (
                <tr className="border-t border-dashed border-[#E4E4E7] text-[11.5px]">
                    <td className="py-1.5 pr-2 text-[#5E636E] italic">Subtotal</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{nzMoney(subtotal.amount)}</td>
                    <td />
                    <td className="py-1.5 pr-2 text-right tabular-nums">{nzMoney(subtotal.paid)}</td>
                    <td className={`py-1.5 pr-2 text-right tabular-nums ${subtotal.overdue > 0 ? "text-red-700" : ""}`}>{nzMoney(subtotal.overdue)}</td>
                    <td />
                </tr>
            )}
        </>
    );
}

/* -------- Attachments (PDF / Excel / etc.) -------- */
function ReportAttachments({ reportId, attachments, isEditable, onChange }) {
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState("");
    const inputRef = useRef(null);

    async function pick(file) {
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8 MB)"); return; }
        setBusy(true);
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(String(r.result));
                r.onerror = () => reject(r.error);
                r.readAsDataURL(file);
            });
            const b64 = dataUrl.split(",", 2)[1];
            await api.post(`/tax-liability/reports/${reportId}/attachments`, {
                filename: file.name,
                mime_type: file.type || "application/octet-stream",
                data_base64: b64,
            });
            toast.success(`Attached ${file.name}`);
            onChange?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
    }

    async function download(att) {
        setDownloading(att.id);
        try {
            const { data } = await api.get(`/tax-liability/reports/${reportId}/attachments/${att.id}`);
            const b64 = data?.data_base64 || "";
            const bin = atob(b64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            const blob = new Blob([bytes], { type: data?.mime_type || att.mime_type });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = att.filename || "attachment";
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDownloading(""); }
    }

    async function remove(att) {
        if (!window.confirm(`Remove "${att.filename}" from this report?`)) return;
        try {
            await api.delete(`/tax-liability/reports/${reportId}/attachments/${att.id}`);
            toast.success("Attachment removed");
            onChange?.();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div className="rounded-md border border-[#EEEDE7] p-3 bg-white" data-testid="tl-attachments">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <h2 className="text-[14px] font-semibold text-[#0F1115]">Attachments <span className="text-[11px] text-[#5E636E] font-normal">(PDF / Excel / CSV / images, max 8 MB each)</span></h2>
                {isEditable && (
                    <>
                        <input ref={inputRef} type="file" className="hidden" accept=".pdf,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,image/*"
                            onChange={(e) => pick(e.target.files?.[0])} />
                        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} className="h-8 text-[12px]" data-testid="tl-attach-add">
                            {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                            Add file
                        </Button>
                    </>
                )}
            </div>
            {attachments.length === 0 ? (
                <div className="text-[12px] text-[#5E636E] italic">No files attached.</div>
            ) : (
                <ul className="divide-y divide-[#EEEDE7]">
                    {attachments.map((a) => (
                        <li key={a.id} className="flex items-center justify-between gap-3 py-2" data-testid={`tl-attach-${a.id}`}>
                            <div className="min-w-0 flex items-center gap-2">
                                <FileText className="h-4 w-4 text-[#5E636E] shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-[13px] font-medium text-[#0F1115] truncate">{a.filename}</div>
                                    <div className="text-[11px] text-[#5E636E]">{Math.round((a.size_bytes || 0) / 1024)} KB · {a.mime_type} · uploaded {nzDate(a.uploaded_at)}{a.uploaded_by_name ? ` by ${a.uploaded_by_name}` : ""}</div>
                                </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={downloading === a.id} onClick={() => download(a)} data-testid={`tl-attach-${a.id}-download`}>
                                    {downloading === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Download"}
                                </Button>
                                {isEditable && (
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#5E636E] hover:text-red-700" onClick={() => remove(a)} data-testid={`tl-attach-${a.id}-remove`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function FieldRow({ children }) { return <div className="grid grid-cols-2 gap-2">{children}</div>; }
function Field({ label, children }) { return <div><Label className="text-[12px] text-[#5E636E]">{label}</Label><div className="mt-1">{children}</div></div>; }

/* -------- Report builder trigger (opens type-picker) -------- */
function ReportBuilderTrigger({ row, onBuilt }) {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(["gst", "income_tax", "provisional_tax", "paye", "other_ird"]);
    const [comments, setComments] = useState("");
    const [recommendations, setRecommendations] = useState("");
    const [busy, setBusy] = useState(false);

    function toggle(k) {
        setSelected((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
    }
    async function generate() {
        if (selected.length === 0) { toast.error("Pick at least one liability type"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/tax-liability/reports", {
                client_id: row.client_id,
                snapshot_id: row.id,
                selected_types: selected,
                comments: comments || null,
                recommendations: recommendations || null,
            });
            toast.success(`Report v${data.report.version_number} generated`);
            setOpen(false);
            onBuilt(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <>
            <Button size="sm" onClick={() => setOpen(true)} className="h-7 text-[11px] bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid={`tl-build-${row.client_id}`}>
                <FileText className="h-3 w-3 mr-1" /> Generate report
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="tl-build-dialog">
                    <DialogHeader>
                        <DialogTitle>Generate report · {row.client_name}</DialogTitle>
                        <DialogDescription>Snapshot v{row.version_number} · captured {nzDate(row.captured_at)}. Pick which liability types to include.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            {TYPES.map((t) => (
                                <label key={t.key} className="flex items-center gap-2 text-[13px] text-[#0F1115]" data-testid={`tl-build-type-${t.key}`}>
                                    <input type="checkbox" checked={selected.includes(t.key)} onChange={() => toggle(t.key)} />
                                    {t.label}
                                </label>
                            ))}
                        </div>
                        <Field label="Comments (visible to client)">
                            <Textarea rows={3} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="tl-build-comments" />
                        </Field>
                        <Field label="Recommendations (visible to client)">
                            <Textarea rows={2} value={recommendations} onChange={(e) => setRecommendations(e.target.value)} data-testid="tl-build-recs" />
                        </Field>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                        <Button onClick={generate} disabled={busy || selected.length === 0} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tl-build-generate">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate & preview"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

/* -------- Report onscreen preview -------- */
function ReportPreview({ reportId, onBack }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [ccFirm, setCcFirm] = useState(true);
    const [editSubject, setEditSubject] = useState("");
    const [editIntro, setEditIntro] = useState("");
    const [commentsDraft, setCommentsDraft] = useState("");
    const [recDraft, setRecDraft] = useState("");
    const [dirty, setDirty] = useState(false);
    const [savingEdits, setSavingEdits] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/tax-liability/reports/${reportId}`);
            setData(data);
            setCommentsDraft(data.report.comments || "");
            setRecDraft(data.report.recommendations || "");
            setDirty(false);
        }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [reportId]);
    useEffect(() => { load(); }, [load]);

    const status = data?.report?.status;
    const isEditable = status === "draft" || status === "reviewed";
    const clientAlias = (data?.client?.alias || "").trim();
    const canApprove = status === "draft" && !!clientAlias;

    async function saveEdits() {
        setSavingEdits(true);
        try {
            const { data: fresh } = await api.patch(`/tax-liability/reports/${reportId}`, {
                comments: commentsDraft,
                recommendations: recDraft,
            });
            setData((prev) => prev ? { ...prev, report: fresh.report } : fresh);
            setDirty(false);
            toast.success("Report edits saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingEdits(false); }
    }

    async function approve() {
        if (!canApprove) {
            toast.error("Add a contact name (alias) to the client profile before approving.");
            return;
        }
        if (dirty) { await saveEdits(); }
        setBusy(true);
        try {
            const { data: fresh } = await api.patch(`/tax-liability/reports/${reportId}`, { status: "approved" });
            setData(fresh);
            toast.success("Report approved — you can now email or publish it");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    function openSendDialog() {
        setCcFirm(true);
        setEditSubject(data?.email_default_subject || "Your Current Tax Liability Summary");
        setEditIntro(data?.email_default_intro || "");
        setSendOpen(true);
    }

    async function confirmSendEmail() {
        setBusy(true);
        try {
            const { data: res } = await api.post(`/tax-liability/reports/${reportId}/send-email`, {
                cc_firm: ccFirm,
                subject_override: editSubject && editSubject !== (data?.email_default_subject || "") ? editSubject : null,
                intro_override: editIntro && editIntro !== (data?.email_default_intro || "") ? editIntro : null,
            });
            const ccMsg = (res.cc && res.cc.length) ? ` (cc ${res.cc.join(", ")})` : "";
            const nameMsg = res.to_name ? `${res.to_name} <${res.to}>` : res.to;
            toast.success(`Emailed to ${nameMsg}${ccMsg}`);
            setSendOpen(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function publish() {
        setBusy(true);
        try {
            await api.post(`/tax-liability/reports/${reportId}/publish`, {});
            toast.success("Published to client portal");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    if (loading || !data) return <div className="p-12 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading report…</div>;

    const { client, snapshot, report, lines, total_liability, selected_types } = data;
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap print:hidden">
                <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs text-[#5E636E] hover:text-[#0F1115]" data-testid="tl-preview-back">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
                </button>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={() => window.print()} data-testid="tl-print"><Printer className="h-4 w-4 mr-1" /> Print / PDF</Button>
                    {report.status === "draft" && (
                        <Button onClick={approve} disabled={busy || !canApprove} title={!canApprove ? "Add a contact name (alias) to the client profile before approving" : ""} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed" data-testid="tl-approve">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</>}
                        </Button>
                    )}
                    {["approved", "reviewed", "sent"].includes(report.status) && (
                        <Button onClick={openSendDialog} disabled={busy} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tl-send-email">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send email{report.email_sent_at ? " again" : ""}</>}
                        </Button>
                    )}
                    {["approved", "sent"].includes(report.status) && (
                        <Button onClick={publish} disabled={busy} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="tl-publish">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish to portal"}
                        </Button>
                    )}
                    {report.status === "published" && (
                        <span className="inline-flex items-center gap-1 text-[12px] text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-3 py-1" data-testid="tl-published-chip">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Published {nzDate(report.portal_published_at)}
                        </span>
                    )}
                    {report.email_sent_at && (
                        <span className="inline-flex items-center gap-1 text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="tl-sent-chip">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Emailed {nzDate(report.email_sent_at)}
                        </span>
                    )}
                </div>
            </div>

            <Card className="p-8 max-w-3xl mx-auto shadow-sm" data-testid="tl-preview">
                <div className="border-b border-[#EEEDE7] pb-5 mb-5">
                    <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#5E636E]">NZ Taxation Limited</div>
                    <h1 className="mt-1 text-2xl font-semibold text-[#0F1115]">Tax Liability Report</h1>
                    <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] text-[#5E636E]">
                        <div><b className="text-[#0F1115]">Client:</b> {client.name}</div>
                        <div><b className="text-[#0F1115]">IRD number:</b> {client.ird_number || "—"}</div>
                        <div><b className="text-[#0F1115]">Report date:</b> {nzDate(report.report_date)}</div>
                        <div><b className="text-[#0F1115]">Prepared by:</b> {report.prepared_by_name || "—"}</div>
                        <div><b className="text-[#0F1115]">Report version:</b> v{report.version_number}</div>
                        <div><b className="text-[#0F1115]">Snapshot:</b> v{snapshot.version_number} · {nzDate(snapshot.captured_at)}</div>
                        <div><b className="text-[#0F1115]">Source:</b> {SOURCES.find((s) => s.key === snapshot.source)?.label || snapshot.source}</div>
                        <div><b className="text-[#0F1115]">Status:</b> {REPORT_STATUS_LABELS[report.status] || report.status}</div>
                    </div>
                </div>

                <div className={`mb-5 rounded-md border p-3 text-[12.5px] ${clientAlias ? "border-emerald-200 bg-emerald-50/60 text-emerald-900" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="tl-addressee">
                    <div className="text-[10.5px] uppercase tracking-wider opacity-70 mb-0.5">Addressee</div>
                    {clientAlias ? (
                        <div>
                            <b>{clientAlias}</b>
                            {client.contact_email ? <span className="opacity-80"> · {client.contact_email}</span> : <span className="opacity-70"> · no email on file</span>}
                        </div>
                    ) : (
                        <div>
                            <b>Missing contact name.</b> Open the client profile and set the <i>Alias</i> field before approving this report.
                        </div>
                    )}
                </div>

                <section>
                    <h2 className="text-[14px] font-semibold text-[#0F1115] mb-2">Tax liability summary</h2>
                    <LinesTable lines={lines} totals={data.totals} categoryTotals={data.category_totals} />
                </section>

                <section className="mt-5">
                    <ReportAttachments reportId={reportId} attachments={report.attachments || []} isEditable={isEditable} onChange={load} />
                </section>

                <section className="mt-5">
                    <div className="flex items-center justify-between mb-1">
                        <h2 className="text-[14px] font-semibold text-[#0F1115]">Comments <span className="text-[11px] text-[#5E636E] font-normal">(visible to client)</span></h2>
                    </div>
                    {isEditable ? (
                        <Textarea
                            rows={3}
                            value={commentsDraft}
                            onChange={(e) => { setCommentsDraft(e.target.value); setDirty(true); }}
                            placeholder="Optional context for the client…"
                            data-testid="tl-preview-comments"
                        />
                    ) : (
                        <p className="text-[13px] text-[#0F1115] whitespace-pre-wrap">{report.comments || <span className="text-[#5E636E] italic">— none —</span>}</p>
                    )}
                </section>

                <section className="mt-5">
                    <h2 className="text-[14px] font-semibold text-[#0F1115] mb-1">Recommendations <span className="text-[11px] text-[#5E636E] font-normal">(visible to client)</span></h2>
                    {isEditable ? (
                        <Textarea
                            rows={2}
                            value={recDraft}
                            onChange={(e) => { setRecDraft(e.target.value); setDirty(true); }}
                            placeholder="Next steps you're suggesting…"
                            data-testid="tl-preview-recs"
                        />
                    ) : (
                        <p className="text-[13px] text-[#0F1115] whitespace-pre-wrap">{report.recommendations || <span className="text-[#5E636E] italic">— none —</span>}</p>
                    )}
                </section>

                {isEditable && (
                    <div className="mt-4 flex items-center justify-end gap-2 print:hidden">
                        {dirty && <span className="text-[11px] text-amber-700" data-testid="tl-preview-dirty">Unsaved changes</span>}
                        <Button size="sm" variant="outline" onClick={saveEdits} disabled={savingEdits || !dirty} data-testid="tl-preview-save">
                            {savingEdits ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" /> Save edits</>}
                        </Button>
                    </div>
                )}

                <div className="mt-6 pt-4 border-t border-[#EEEDE7] text-[11px] text-[#5E636E]">
                    This report reflects the latest information available at the time it was prepared. Balances may change if IRD processes future assessments, transfers or adjustments.
                    Includes: {selected_types.map((t) => TYPES.find((x) => x.key === t)?.label).filter(Boolean).join(" · ")}.
                </div>
            </Card>

            <Dialog open={sendOpen} onOpenChange={(v) => !busy && setSendOpen(v)}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="tl-send-dialog">
                    <DialogHeader>
                        <DialogTitle>Send tax liability report</DialogTitle>
                        <DialogDescription>
                            {clientAlias
                                ? <>Emailing <b>{clientAlias}</b>{client.contact_email ? <> at <span className="tabular-nums">{client.contact_email}</span></> : null} via Outlook.</>
                                : <span className="text-red-700">Client has no contact name (alias) on file — set one before sending.</span>}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-[13px]">
                        <Field label="Subject">
                            <Input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} data-testid="tl-send-subject" />
                        </Field>
                        <Field label="Email body (intro)">
                            <Textarea rows={8} value={editIntro} onChange={(e) => setEditIntro(e.target.value)} className="font-mono text-[12.5px] leading-relaxed" data-testid="tl-send-intro" />
                            <div className="mt-1 text-[11px] text-[#5E636E]">The liability table, comments and recommendations are appended automatically below this intro.</div>
                        </Field>
                        <label className="flex items-start gap-2 rounded-md border border-[#EEEDE7] bg-[#FAFAF7] p-3 cursor-pointer" data-testid="tl-send-cc-firm-row">
                            <input
                                type="checkbox"
                                checked={ccFirm}
                                onChange={(e) => setCcFirm(e.target.checked)}
                                className="mt-0.5 h-3.5 w-3.5 accent-[#0F1115]"
                                data-testid="tl-send-cc-firm"
                            />
                            <span>
                                <div className="font-medium text-[#0F1115]">Send a copy to <span className="tabular-nums">info@nztaxation.nz</span></div>
                                <div className="text-[11.5px] text-[#5E636E]">Adds the firm inbox as CC so the office has an on-file copy. Recommended.</div>
                            </span>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSendOpen(false)} disabled={busy}>Cancel</Button>
                        <Button onClick={confirmSendEmail} disabled={busy || !clientAlias || !client.contact_email} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tl-send-confirm">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send email"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* -------- Admin bulk delete for selected clients -------- */
function BulkDeleteDialog({ open, onClose, clients, onDone }) {
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setConfirm(""); }, [open]);
    async function submit() {
        if (confirm !== "DELETE") { toast.error('Type "DELETE" to confirm'); return; }
        if (!clients.length) { toast.error("No clients selected"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/tax-liability/admin/bulk-delete", {
                confirm,
                client_ids: clients.map((c) => c.id),
            });
            toast.success(`Deleted ${data.deleted_snapshots} snapshot(s) + ${data.deleted_reports} report(s) across ${data.clients} client(s)`);
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="tl-bulk-delete-dialog">
                <DialogHeader>
                    <DialogTitle className="text-red-800">Delete tax liability data for {clients.length} client{clients.length === 1 ? "" : "s"}?</DialogTitle>
                    <DialogDescription>
                        <b>THIS CANNOT BE UNDONE.</b> Every snapshot and every report for the selected clients will be permanently deleted. The audit log is preserved.
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-40 overflow-y-auto rounded-md border border-red-200 bg-red-50/40 p-2 space-y-1" data-testid="tl-bulk-delete-list">
                    {clients.map((c) => (
                        <div key={c.id} className="text-[12.5px] text-red-900">• {c.name}</div>
                    ))}
                </div>
                <Field label='Type "DELETE" to confirm'>
                    <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="tl-bulk-delete-confirm" />
                </Field>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || confirm !== "DELETE"} className="bg-red-700 hover:bg-red-800 text-white" data-testid="tl-bulk-delete-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1" /> Delete {clients.length} client{clients.length === 1 ? "" : "s"}</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
function AdminClearDialog({ open, onClose, onDone }) {
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    async function submit() {
        if (confirm !== "DELETE") { toast.error('Type "DELETE" to confirm'); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/tax-liability/admin/clear", { confirm, scope: "all" });
            toast.success(`Cleared ${data.deleted_snapshots} snapshot(s) + ${data.deleted_reports} report(s)`);
            setConfirm(""); onClose(); onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="tl-admin-dialog">
                <DialogHeader>
                    <DialogTitle className="text-red-800">Clear Tax Liability database</DialogTitle>
                    <DialogDescription>
                        <b>WARNING: THIS ACTION CANNOT BE UNDONE.</b> Every liability snapshot and every report in your tenant will be deleted. The audit log is preserved.
                    </DialogDescription>
                </DialogHeader>
                <Field label='Type "DELETE" to confirm'>
                    <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="tl-admin-confirm" />
                </Field>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || confirm !== "DELETE"} className="bg-red-700 hover:bg-red-800 text-white" data-testid="tl-admin-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1" /> Clear database</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
